/**
 * 현재 사용자가 팔로잉하는 목록만 보여주고 있다.
 * 그래서 과거에 구현한 팔로워 목록 보여주는 기능 "가능하지만" 호출되지 않고 있다.
 */

import { /*Map,*/ fromJS } from "immutable";
import { handleActions, createAction } from "redux-actions";
import Restful from "lib/restful";
import config from "lib/fr-config";
import { sleep } from "lib/commonUtil";

const UNIT_LENGTH = config.followUserList.LENGTH;
const EXP_MINUTE = 30;
const WAIT_MILLISEC = config.storeModule.waitForLoadingMilliSec;

const SET_IS_LOADING = "followUserList/SET_IS_LOADING";
const SET_IS_LIST_LOADING = "followUserList/SET_IS_LIST_LOADING";
const SET_NOW_TYPE = "followUserList/SET_NOW_TYPE";
const INIT_EXPORTED_DATA = "followUserList/INIT_EXPORTED_DATA";
const SET_NEW_LIST_DATA = "followUserList/SET_NEW_LIST_DATA";
const ADD_LIST = "followUserList/ADD_LIST";
const SET_IS_END = "followUserList/SET_IS_END";
const SET_EXPIRATION_DATE = "followUserList/SET_EXPIRATION_DATE";
const EXPORT_USER_LIST = "followUserList/EXPORT_USER_LIST";
const INIT_ALL_DATA = "followUserList/INIT_ALL_DATA";

const setIsLoading = createAction(SET_IS_LOADING);
const setIsListLoading = createAction(SET_IS_LIST_LOADING);
const setNowType = createAction(SET_NOW_TYPE);
const initExportedData = createAction(INIT_EXPORTED_DATA);
const setNewListData = createAction(SET_NEW_LIST_DATA);
const addList = createAction(ADD_LIST);
const setIsEnd = createAction(SET_IS_END);
const setExpirationDate = createAction(SET_EXPIRATION_DATE);
const exportUserList = createAction(EXPORT_USER_LIST);
const initAllData = createAction(INIT_ALL_DATA);

const getFollowUserListState = ({ getState }) => {
  const followUserListState = getState().followUserList.toJS();
  return followUserListState;
};

const getTargetData = ({ getState, type }) => {
  const followUserListState = getFollowUserListState({ getState });
  const { data } = followUserListState;
  return data[type];
};

const getTargetLoadingData = ({ getState, type }) => {
  const followingTargetData = getTargetData({ getState, type });
  const isListLoading =
    followingTargetData && followingTargetData.isListLoading;
  return isListLoading;
};

const getLastUser = list => {
  if (list.length === 0) return undefined;
  const length = list.length;
  return list[length - 1]["username"];
};

const getNowType = ({ getState }) => {
  const followUserListState = getFollowUserListState({ getState });
  const { nowType } = followUserListState;
  return nowType;
};

const getIsAllLoadingDone = ({ getState }) => {
  const followingTargetData = getTargetData({ getState, type: "following" });
  const followerTargetData = getTargetData({ getState, type: "follower" });
  const isFollowingListLoading =
    followingTargetData && followingTargetData.isListLoading;
  const isFollowerListLoading =
    followerTargetData && followerTargetData.isListLoading;
  return !isFollowingListLoading && !isFollowerListLoading;
};

const checkExpDate = ({ getState, type }) => {
  const targetData = getTargetData({ getState, type });
  const expirationDate = targetData && targetData.expirationDate;
  if (!expirationDate) return { needUpdate: true };
  const nowDate = new Date();
  return { needUpdate: nowDate > expirationDate ? true : false };
};

const setExpDate = ({ dispatch, type }) => {
  const expirationDate = new Date();
  expirationDate.setMinutes(expirationDate.getMinutes() + EXP_MINUTE);
  dispatch(setExpirationDate({ type, expirationDate }));
};

const addNewList = async ({ type, dispatch, getState, idToken }) => {
  if (type === "follower") {
    /** 현재 type값으로 "follower"가 설정될 일 없기에 임시로 에러처리 */
    console.error("unauthorized type", type);
  }

  const targetData = getTargetData({ getState, type });
  const { savedList } = targetData;
  const length = UNIT_LENGTH;
  const lastUser = getLastUser(savedList);

  let backendFunc;
  switch (type) {
    case "following":
      backendFunc = Restful.getFollowingsAPI;
      break;
    case "follower":
      backendFunc = Restful.getFollowersAPI;
      break;
    default:
      throw Error(`wrong type : ${type}`);
  }

  const { code, res, errInfo } = await backendFunc({
    idToken,
    lastUser,
    length
  });
  if (code === "error") {
    const errObj = Error("api error");
    errObj.errorValue = { ...errInfo };
    throw errObj;
  }

  const { list } = res;

  dispatch(addList({ type, list }));
  dispatch(setIsEnd({ type, length: list.length }));
};

export const changeFollowList =
  ({ type, idToken }) =>
  async (dispatch, getState) => {
    if (type !== "following" && type !== "follower") return null;

    let isListLoading = getTargetLoadingData({ getState, type });

    while (isListLoading) {
      await sleep(WAIT_MILLISEC);
      isListLoading = getTargetLoadingData({ getState, type });
    }

    dispatch(setIsListLoading({ type, isListLoading: true }));
    dispatch(setIsLoading(true));
    dispatch(setNowType(type));

    const { needUpdate } = checkExpDate({ getState, type });

    if (needUpdate) {
      dispatch(initExportedData());
      dispatch(setNewListData(type));
      try {
        await addNewList({ type, dispatch, getState, idToken });
      } catch (error) {
        dispatch(setIsListLoading({ type, isListLoading: false }));
        throw error;
      }
      setExpDate({ dispatch, type });
    }

    const nowType = getNowType({ getState });
    if (nowType === type) {
      dispatch(exportUserList(type));
    }

    dispatch(setIsListLoading({ type, isListLoading: false }));

    const isAllLoadingDone = getIsAllLoadingDone({ getState });
    if (isAllLoadingDone) {
      dispatch(setIsLoading(false));
    }
  };

export const getList = idToken => async (dispatch, getState) => {
  const type = getNowType({ getState });
  const targetData = getTargetData({ getState, type });
  const { isListLoading, isEnd } = targetData;

  if (isEnd || isListLoading) return null;

  dispatch(setIsListLoading({ type, isListLoading: true }));
  dispatch(setIsLoading(true));
  try {
    await addNewList({ type, dispatch, getState, idToken });
  } catch (error) {
    dispatch(setIsListLoading({ type, isListLoading: false }));
    throw error;
  }

  const nowType = getNowType({ getState });
  if (nowType === type) {
    dispatch(exportUserList(type));
  }

  dispatch(setIsListLoading({ type, isListLoading: false }));

  const isAllLoadingDone = getIsAllLoadingDone({ getState });
  if (isAllLoadingDone) {
    dispatch(setIsLoading(false));
  }
};

export const deleteAllData = () => (dispatch, getState) => {
  dispatch(initAllData());
};

const initialState = fromJS({
  data: {},
  /*
  data 의 구조.
  data: {
    following: {
      savedList: [],
      isListLoading: false,
      isEnd: false,
      expirationDate: null
    },
    follower: {...}
  },
  */

  userList: [],
  nowType: null,
  isLoading: false,
  isEnd: false
});

export default handleActions(
  {
    [SET_IS_LOADING]: (state, action) => {
      const isLoading = action.payload;
      return state.set("isLoading", fromJS(isLoading));
    },
    [SET_IS_LIST_LOADING]: (state, action) => {
      const { type, isListLoading } = action.payload;
      return state.setIn(
        ["data", type, "isListLoading"],
        fromJS(isListLoading)
      );
    },
    [SET_NOW_TYPE]: (state, action) => {
      const nowType = action.payload;
      return state.set("nowType", fromJS(nowType));
    },
    [INIT_EXPORTED_DATA]: (state, action) => {
      const { userList, isEnd } = initialState.toJS();
      return state
        .set("userList", fromJS(userList))
        .set("isEnd", fromJS(isEnd));
    },
    [SET_NEW_LIST_DATA]: (state, action) => {
      const type = action.payload;
      const newData = {
        savedList: [],
        isEnd: false,
        expirationDate: null
      };
      return state.setIn(["data", type], fromJS(newData));
    },
    [ADD_LIST]: (state, action) => {
      const { type, list } = action.payload;
      const { data } = state.toJS();
      const { savedList } = data[type];
      return state.setIn(
        ["data", type, "savedList"],
        fromJS(savedList.concat(list))
      );
    },
    [SET_IS_END]: (state, action) => {
      const { type, length } = action.payload;
      const isEnd = length === UNIT_LENGTH ? false : true;
      return state.setIn(["data", type, "isEnd"], fromJS(isEnd));
    },
    [SET_EXPIRATION_DATE]: (state, action) => {
      const { type, expirationDate } = action.payload;
      return state.setIn(
        ["data", type, "expirationDate"],
        fromJS(expirationDate)
      );
    },
    [EXPORT_USER_LIST]: (state, action) => {
      const type = action.payload;
      const { data } = state.toJS();
      const { savedList, isEnd } = data[type];
      return state
        .set("userList", fromJS(savedList))
        .set("isEnd", fromJS(isEnd));
    },
    [INIT_ALL_DATA]: (state, action) => {
      return initialState;
    }
  },
  initialState
);
